<template>
  <div>
    <div class="cn-listing__headers">
      <h3>
        {{ `CIS Control ${control.number} - ${control.title}` }}
        <a
          class="tooltip"
          v-if="control.description"
          :tooltipcn="`<div class='tooltip-header'>CIS Control ${control.number} - ${control.title}</div>${control.description}`"
        ></a>
      </h3>
      <div class="header-utility">
        <div>
          {{ `${control.safeguardsChecked}/${control.safeguardsTotal} Safeguards` }}
        </div>
        <div
          v-if="!hideUnselectedSafeguards"
          class="clickable"
          @click="toggleHideUnselectedControlSafeguards(control.id)"
          :disabled="!control.hasUnselectedSafeguards"
        >
          {{
            `${
              hideUnselectedSafeguards || hideUnselectedControlSafeguards ? 'Show' : 'Hide'
            } Unselected`
          }}
        </div>
      </div>
    </div>
    <div class="safeguards">
      <cn-safeguard
        v-for="safeguard in filterSafeguards(control.safeguards)"
        v-bind="$attrs"
        :key="safeguard.id"
        :safeguard="safeguard"
        :isSafeguardSelected="selectedSafeguards.includes(safeguard.id)"
        :getSafeguardMappings="getSafeguardMappings"
      />
    </div>
  </div>
</template>

<script>
import tippy from 'tippy.js';
import { hideAll } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import CISControlsNavigatorListingSafeguard from './CIS-Controls-Navigator-Listing-Safeguard.vue';

export default {
  name: 'CIS-Controls-Navigator-Listing-Control',
  components: {
    CnSafeguard: CISControlsNavigatorListingSafeguard
  },
  props: {
    data: {
      type: Object,
      default: () => {}
    },
    selectedSafeguards: {
      type: Array,
      default: () => []
    },
    hideUnselectedSafeguards: {
      type: Boolean,
      default: false
    },
    hideUnselectedControlSafeguards: {
      type: Boolean,
      default: false
    },
    getSafeguardMappings: {
      type: Function,
      default: () => []
    }
  },
  emits: ['toggleHideUnselectedControlSafeguards'],
  mounted() {
    tippy('[tooltipcn]', {
      content: (reference) =>
        reference.getAttribute('tooltipcn') + '<div class="tooltip-close">X</div>',
      allowHTML: true,
      interactive: true,
      interactiveDebounce: 75,
      maxWidth: 500,
      placement: 'top-end',
      touch: true,
      onCreate(instance) {
        instance.popper.querySelector('.tooltip-close').onclick = () => {
          instance.hide();
        };
      },
      onShow() {
        hideAll();
      }
    });
  },
  computed: {
    control() {
      const safeguardsChecked =
        this.data.safeguards?.filter((s) => this.selectedSafeguards.includes(s.id))?.length ?? 0;
      const safeguardsTotal = this.data.safeguards?.length ?? 0;
      const hasUnselectedSafeguards = safeguardsChecked < safeguardsTotal;

      const output = {
        id: this.data.id,
        number: this.data.number?.numberValue,
        title: this.data.title?.value,
        description: this.data.description?.value,
        safeguards: this.data.safeguards,
        safeguardsChecked: safeguardsChecked,
        safeguardsTotal: safeguardsTotal,
        hasUnselectedSafeguards: hasUnselectedSafeguards
      };

      return output;
    }
  },
  methods: {
    filterSafeguards(safeguards) {
      if (this.hideUnselectedSafeguards || this.hideUnselectedControlSafeguards) {
        return safeguards.filter((s) => {
          return this.selectedSafeguards.includes(s.id);
        });
      }

      return safeguards;
    },
    toggleHideUnselectedControlSafeguards(controlId) {
      this.$emit('toggleHideUnselectedControlSafeguards', controlId);
    }
  }
};
</script>

<style lang="scss" scoped>
.cn-listing {
  &__headers {
    background-color: $box-gray;
    padding: 1.6rem 2rem 1.8rem 2rem;

    @include tablet-up {
      display: flex;
      justify-content: space-between;
      padding: 2rem 4rem 2rem 4rem;
    }

    h3 {
      margin: 0 0 1rem 0;
      display: inline-block;
      font-size: 1.8rem;
      line-height: 2.4rem;
      padding-right: 2rem;

      @include tablet-up {
        margin: 0 0.5rem 0 0;
        font-size: 2.8rem;
        line-height: 3.5rem;
        padding-right: 0;
      }

      &::before {
        display: none;
      }
    }

    .tooltip {
      top: 0.6rem;
    }

    .header-utility {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      @include tablet-up {
        display: block;
        flex-direction: column;
        text-align: right;
      }

      > * {
        @include font-styles('Arial', 1.4rem, 500, normal, 2rem, 0, $dark-blue);
        white-space: nowrap;
      }

      .clickable {
        cursor: pointer;
        color: $blue;

        &[disabled='true'] {
          pointer-events: none;
          color: $lite-gray;
        }
      }
    }

    &:deep() {
      .tippy-box {
        .tippy-content {
          @include font-styles('Arial', 1.8rem, 300, normal, 2.2rem, 0, $white);

          .tooltip-header {
            @include font-styles('Arial', 1.8rem, 500, normal, 2.2rem, 0, $white);
            margin-bottom: 1.2rem;
          }
        }
      }

      .tippy-box[data-placement^='bottom-start'] {
        @include tablet-up {
          left: -1rem;
        }
      }

      .tippy-box[data-placement^='bottom-end'] {
        @include tablet-up {
          left: 2.5rem;
        }
      }

      .tippy-box[data-placement^='top-start'] {
        top: -3rem;

        @include tablet-up {
          left: -1rem;
        }
      }

      .tippy-box[data-placement^='top-end'] {
        top: -3rem;

        @include tablet-up {
          left: 2rem;
        }
      }
    }
  }

  .safeguards {
    padding: 1.6rem 2rem;

    @include tablet-up {
      padding: 2.5rem 4rem 0 4rem;
    }
  }
}
</style>
